import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AlertService } from 'src/core/services/alert.service';
import { CompanyService } from 'src/core/services/company.service';
import { IndividualService } from 'src/core/services/individual.service';
import { LoaderService } from 'src/core/services/loader.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces, Province } from 'src/form/components/data/provinces';
import { AddressCreatePayload } from 'src/interfaces/address';
import { Company, CompanyType } from 'src/interfaces/company';
import { DeliveryType } from 'src/interfaces/delivery-type';
import { IndivdiualCreatePayload, Individual, IndividualType } from 'src/interfaces/individual';

@Component({
  selector: 'app-client-create',
  templateUrl: './individual-create.component.html',
  styleUrls: ['./individual-create.component.scss']
})
export class IndividualCreateComponent implements OnInit {
  countries: Country[] = all_countries;
  provinces: Province[] = all_provinces;
  filteredCountries: Observable<Country[]>;
  deliveryTypes: DeliveryType[] = [];
  companyTypes: CompanyType[] = [];
  individualTypes: IndividualType[] = [];
  companies: Company[] = [];
  selectedCompany: Company | null = null;
  /** Required: every individual belongs to a distributor (DIS company). */
  selectedDistributor: Company | null = null;
  filteredCountriesForIndividual: Observable<Country[]>;

  individualDetailsForm = this._formBuilder.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')])],
    phoneNumberAlt: ['', Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    numberOfDependants: ['0', Validators.min(0)],
    companyId: [''],
    individualType: ['', Validators.required],
    isAnimal: ['', Validators.required]
  });

  addressDetailsForm = this._formBuilder.group({
    country: ['South Africa', Validators.required],
    province: ['', Validators.required],
    city: [''],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    buildingAptNumber: ['', Validators.required],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])],
  });



  isLoading: boolean = false;

  constructor(
    private _prescriptionService: PrescriptionService,
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _alertService: AlertService,
    private _individualService: IndividualService,
    private _companyService: CompanyService,
    private _router: Router
  ) { }

  ngOnInit(): void {

    this
      ._prescriptionService
      .getFormData([
        'company_type',
        'delivery_type',
        'individual_type'
      ])
      .subscribe(data => {
        this.deliveryTypes = data.delivery_type;
        this.companyTypes = data.company_type;
        this.individualTypes = data.individual_type;
      });

    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.filteredCountriesForIndividual = this
      .addressDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

  }


  companySelected(event: MatAutocompleteSelectedEvent) {
    this.selectedCompany = event.option.value;
    
    if (!this.selectedCompany) return;
    this
      .individualDetailsForm
      .patchValue({
        companyName: this.selectedCompany.name,
        companyId: this.selectedCompany.id
      });

  }

  async save() {

    const isAnimal: boolean = Boolean(
      parseInt(this.individualDetailsForm.value.isAnimal)
    );

    if (this.individualDetailsForm.invalid || this.addressDetailsForm.invalid) return;

    if (!this.selectedDistributor || this.selectedDistributor.id == null) {
      this._alertService.errorAlert('Please select a distributor.');
      return;
    }

    const addressPayload: AddressCreatePayload = {
      building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
      address_1: this.addressDetailsForm.value.addressLine1,
      address_2: this.addressDetailsForm.value.addressLine2,
      province: this.addressDetailsForm.value.province,
      city: this.addressDetailsForm.value.city,
      postcode: this.addressDetailsForm.value.postcode,
      country: this.addressDetailsForm.value.country
    }

    const payload: IndivdiualCreatePayload = {
      first_name: this.individualDetailsForm.value.firstName,
      last_name: this.individualDetailsForm.value.lastName,
      gender: this.individualDetailsForm.value.gender,
      title: this.individualDetailsForm.value.title,
      initials: this.individualDetailsForm.value.initials,
      dependant_number: this.individualDetailsForm.value.numberOfDependants,
      date_of_birth: this.individualDetailsForm.value.dateOfBirth,
      phone_number: this.individualDetailsForm.value.phoneNumber,
      phone_number_alt: this.individualDetailsForm.value.phoneNumberAlt,
      individual_type_id: this.individualDetailsForm.value.individualType ? parseInt(this.individualDetailsForm.value.individualType) : undefined,
      company_id: this.selectedCompany ? this.selectedCompany.id : undefined,
      distributor_id: this.selectedDistributor.id as number,
      is_animal: isAnimal,
      email: this.individualDetailsForm.value.email,
      email_alt: this.individualDetailsForm.value.emailAlt,
      address: addressPayload
    }

    const individual: Individual | null = await this._individualService.createIndividual(payload);

    if (individual) {
      this._router
        .navigateByUrl('/', { skipLocationChange: true })
        .then(() => {
          this._router.navigate(['/portal/individuals/create']);
        })
    }
  }

  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  async openSearchCompanyDialog() {
    this.selectedCompany = await this._companyService
      .openCompanySearchDialog();

  }

  deselectCompany() {
    this.selectedCompany = null;
  }

  async openSearchDistributorDialog() {
    this.selectedDistributor = await this._companyService.openCompanySearchDialog(
      'Search for a distributor',
      '',
      'DIS'
    );
  }

  deselectDistributor() {
    this.selectedDistributor = null;
  }

}
